import React, { useEffect, useState } from 'react';
import api from './api';

// The list of every verified order, expandable for the details: who, when, how long,
// what was scanned, and where the PDF went. Shared by the Logs page (platform admin,
// every org, with the errors logged against each order) and the Stats page (an org's
// own orders).

const formatDuration = (startedAt, completedAt) => {
  const mins = Math.round((new Date(completedAt) - new Date(startedAt)) / 60000);
  if (!(mins > 0)) return null;
  return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)} h ${mins % 60} min`;
};

const parseDetails = (json) => {
  try { return json ? JSON.parse(json) : null; } catch { return null; }
};

// Striven wouldn't take the order, so someone marked it verified in Striven by hand
// (the Unfinished Orders tab saw it done in Striven, or someone ticked it off).
export const markedByHand = (v) => {
  const delivery = parseDetails(v.detailsJson)?.delivery;
  return !v.strivenSynced && !!(delivery?.markedInStriven || delivery?.manualDone);
};

// Where the PDF went and whether each step worked, as [label, text, failed].
const deliveryRows = (v, delivery) => {
  const rows = [[
    'Striven order',
    v.strivenSynced
      ? 'Marked verified'
      : markedByHand(v)
        ? 'Marked verified in Striven by hand, since Striven would not accept it from 2Check'
      : delivery?.strivenRejected
        ? 'Striven rejected the order, so it needs marking by hand (see the Unfinished Orders tab).'
        : 'Not updated. Striven sync was turned off when this was verified.',
    !v.strivenSynced && !markedByHand(v),
  ]];
  if (!delivery) return rows;
  const attachment = {
    attached: 'Attached to the order',
    failed: 'Failed to attach',
    'sync off': 'Skipped because Striven sync was off',
  }[delivery.strivenAttachment] || 'Not requested';
  rows.push(['PDF in Striven', attachment, delivery.strivenAttachment === 'failed']);
  rows.push(['PDF upload', delivery.storage ? `Uploaded to ${delivery.storage}` : 'Not requested', false]);
  const to = delivery.emailTo ? ` to ${delivery.emailTo}` : '';
  const email = {
    sent: `Sent${to}`,
    failed: `Failed to send${to}`,
    pending: `Requested${to}, result not reported`,
  }[delivery.email] || 'Not requested';
  rows.push(['PDF email', email, delivery.email === 'failed']);
  rows.push(['PDF download', delivery.download ? 'Downloaded on the tablet' : 'Not requested', false]);
  return rows;
};

// errors + summarizeError are optional: the Logs page passes the logged errors so each
// order shows what went wrong with it.
export function VerificationRows({ verifications, errors = [], summarizeError, showOrg = false }) {
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (key) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  return (
    <div style={s.list}>
      {verifications.map((v, i) => {
        const key = `v-${v.id ?? i}`;
        const open = expanded.has(key);
        const duration = formatDuration(v.startedAt, v.completedAt);
        const orderErrors = errors.filter(e => e.order === String(v.orderId) && e.org === v.orgSlug);
        const extra = parseDetails(v.detailsJson);
        const lineItems = Array.isArray(extra?.items) ? extra.items : [];
        const details = [
          ['Verified by', [v.verifiedBy, v.role].filter(Boolean).join(' · ')],
          ['Started', v.startedAt && new Date(v.startedAt).toLocaleString()],
          ['Finished', new Date(v.completedAt).toLocaleString()],
          ['Time taken', duration],
          ['Items scanned', v.itemCount ? `${v.itemCount} (${v.uniqueItemCount} different products)` : null],
          ['Packed as', [
            v.individualCount && `${v.individualCount} individual`,
            v.subSetCount && `${v.subSetCount} sub-set`,
            v.setCount && `${v.setCount} set`,
          ].filter(Boolean).join(', ')],
          ['Photos taken', extra?.photoCount != null ? String(extra.photoCount) : null],
        ].filter(([, value]) => value);
        return (
          <div key={key} style={s.row}>
            <button type="button" style={s.rowBtn} aria-expanded={open} onClick={() => toggle(key)}>
              <div style={s.rowHead}>
                <span style={{ ...s.chevron, transform: open ? 'rotate(90deg)' : 'none' }}>▸</span>
                <span style={{
                  ...s.badge,
                  background: v.strivenSynced ? '#ecfdf3' : markedByHand(v) ? '#eff6ff' : '#fff7ed',
                  color: v.strivenSynced ? '#15803d' : markedByHand(v) ? '#1d4ed8' : '#c2410c',
                }}>
                  {v.strivenSynced ? 'In Striven' : markedByHand(v) ? 'Marked by hand *' : 'Not in Striven'}
                </span>
                <span style={s.orderTag}>Order #{v.orderId}</span>
                {v.orderName && <span style={s.orderName}>{v.orderName}</span>}
                {showOrg && <span style={s.orgTag}>{v.orgSlug}</span>}
                {orderErrors.length > 0 && (
                  <span style={s.errorTag}>{orderErrors.length} error{orderErrors.length > 1 ? 's' : ''} logged</span>
                )}
                <span style={s.time}>
                  {v.verificationType === 'level2' ? 'Level 2' : 'Level 1'} · {v.verifiedBy} · {new Date(v.completedAt).toLocaleString()}
                </span>
              </div>
            </button>
            {open && (
              <div style={s.details}>
                {details.map(([label, value]) => (
                  <div key={label} style={s.detailRow}>
                    <span style={s.detailKey}>{label}</span>
                    <span>{value}</span>
                  </div>
                ))}
                <div style={s.detailRow}>
                  <span style={s.detailKey}>PDF</span>
                  {v.pdfUrl
                    ? <a href={v.pdfUrl} target="_blank" rel="noreferrer">Open packing list PDF</a>
                    : <span style={{ color: '#999' }}>No saved link</span>}
                </div>
                {deliveryRows(v, extra?.delivery).map(([label, text, failed]) => (
                  <div key={label} style={s.detailRow}>
                    <span style={s.detailKey}>{label}</span>
                    <span style={failed ? s.failed : undefined}>{text}</span>
                  </div>
                ))}
                {lineItems.length > 0 ? (
                  <div style={s.tableWrap}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={{ ...s.th, textAlign: 'left' }}>Item</th>
                          <th style={s.th}>Expected</th>
                          <th style={s.th}>Scanned</th>
                          <th style={s.th}>Photos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((item, j) => {
                          const diff = (item.scanned || 0) - (item.expected || 0);
                          return (
                            <tr key={j}>
                              <td style={{ ...s.td, textAlign: 'left' }}>{item.name}</td>
                              <td style={s.td}>{item.expected}</td>
                              <td style={{ ...s.td, ...(diff ? s.failed : {}) }}>
                                {item.scanned}{diff ? ` (${diff > 0 ? `${diff} over` : `${-diff} short`})` : ''}
                              </td>
                              <td style={s.td}>{item.photos || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={s.note}>
                    Item-by-item detail and PDF delivery results are only recorded for orders verified after this was added.
                  </div>
                )}
                {orderErrors.length > 0 && summarizeError && (
                  <div style={s.detailRow}>
                    <span style={s.detailKey}>Errors on this order</span>
                    <span>
                      {orderErrors.map((e, j) => (
                        <div key={j}>{new Date(e.timestamp).toLocaleString()}: {summarizeError(e)}</div>
                      ))}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// The Stats page's section: loads this org's verification log and lets you search it.
export default function VerificationLog({ msalInstance }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [syncFilter, setSyncFilter] = useState('all');
  const [personFilter, setPersonFilter] = useState('all');

  const getAuthHeaders = async () => {
    const authProvider = localStorage.getItem('authProvider') || 'microsoft';
    if (authProvider === 'google') {
      return { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
    }
    const configRes = await api.get('/api/config/azure');
    const { clientId } = configRes.data;
    const accounts = msalInstance.getAllAccounts();
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [`api://${clientId}/access_as_user`],
      account: accounts[0],
    });
    return { Authorization: `Bearer ${tokenResponse.accessToken}` };
  };

  useEffect(() => {
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await api.get('/api/verifications/log', { headers });
        setRows(res.data || []);
      } catch (err) {
        console.error('Failed to load the verification log:', err);
        setError('Could not load the verification log.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const people = [...new Set(rows.map(v => v.verifiedBy).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const needle = query.trim().toLowerCase();
  const visible = rows.filter(v => {
    if (personFilter !== 'all' && v.verifiedBy !== personFilter) return false;
    if (syncFilter === 'not-synced' && (v.strivenSynced || markedByHand(v))) return false;
    if (syncFilter === 'synced' && !(v.strivenSynced || markedByHand(v))) return false;
    if (!needle) return true;
    return [v.orderId, v.orderName, v.verifiedBy].some(t => String(t || '').toLowerCase().includes(needle));
  });

  if (loading) return <p style={s.empty}>Loading the verification log…</p>;
  if (error) return <p style={s.errorText}>{error}</p>;

  return (
    <div>
      <div style={s.controls}>
        <input
          id="verification-log-search"
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search order number, name or person"
          style={s.search}
        />
        {people.length > 1 && (
          <select id="verification-log-person" value={personFilter} onChange={e => setPersonFilter(e.target.value)} style={s.select} aria-label="Person">
            <option value="all">All people</option>
            {people.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
        <select id="verification-log-filter" value={syncFilter} onChange={e => setSyncFilter(e.target.value)} style={s.select}>
          <option value="all">All verifications</option>
          <option value="not-synced">Not updated in Striven</option>
          <option value="synced">Updated in Striven</option>
        </select>
      </div>
      <p style={s.sub}>
        Every order verified in 2Check, newest first (the last {rows.length >= 300 ? '300' : rows.length}). Tap one for the details.
        "Not in Striven" means Striven wouldn't accept it, so it needs marking there by hand;
        "Marked by hand *" means a person marked it in Striven, not 2Check.
      </p>
      {visible.length === 0
        ? <p style={s.empty}>No verifications to show.</p>
        : <VerificationRows verifications={visible} />}
    </div>
  );
}

const s = {
  controls: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
  search: { flex: '1 1 220px', padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 },
  select: { padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 },
  sub: { fontSize: 13, color: '#888', marginBottom: 14, marginTop: 0, lineHeight: 1.5 },
  errorText: { color: '#b91c1c', fontSize: 14 },
  empty: { color: '#aaa', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  row: { background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '14px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  rowHead: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  rowBtn: { display: 'block', width: '100%', padding: 0, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' },
  chevron: { fontSize: 12, color: '#999', transition: 'transform 0.15s', display: 'inline-block' },
  badge: { fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, textTransform: 'uppercase', letterSpacing: 0.3 },
  orderTag: { fontSize: 13, fontWeight: 600, color: '#333' },
  orderName: { fontSize: 13, color: '#555' },
  orgTag: { fontSize: 12, color: '#666', background: '#f3f4f6', padding: '2px 8px', borderRadius: 100 },
  errorTag: { fontSize: 12, color: '#b91c1c', background: '#fef2f2', padding: '2px 8px', borderRadius: 100 },
  time: { fontSize: 12, color: '#999', marginLeft: 'auto' },
  details: { marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#444' },
  detailRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  detailKey: { width: 150, flexShrink: 0, color: '#888' },
  failed: { color: '#b91c1c', fontWeight: 600 },
  note: { fontSize: 12, color: '#999' },
  tableWrap: { overflowX: 'auto', marginTop: 6 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' },
  th: { textAlign: 'right', padding: '6px 10px', borderBottom: '1px solid #e5e7eb', color: '#888', fontWeight: 600, fontSize: 12 },
  td: { textAlign: 'right', padding: '6px 10px', borderBottom: '1px solid #f3f4f6' },
};
